import React, { useState } from "react";
import { CharacterDiceTemplate, FateFormula, FateFormulaRequest } from "../../../types/types";
import { ConfirmationDialog } from "../../ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash, faPlus, faDice } from "@fortawesome/free-solid-svg-icons";
import DiceTemplateForm from "../DiceTemplate/DiceTemplateForm";

// ponytail: cast FateFormula → CharacterDiceTemplate so DiceTemplateForm can be reused;
// the form only reads the 5 shared fields, so the extra CharacterDiceTemplate fields are unused.
const toTemplate = (f: FateFormula): CharacterDiceTemplate => f as unknown as CharacterDiceTemplate;

export interface FateFormulaLibraryProps {
  formulas: FateFormula[];
  readOnly?: boolean;
  search: string;
  onSearch: (_s: string) => void;
  onCreate: (_r: FateFormulaRequest) => Promise<void>;
  onUpdate: (_id: number, _r: FateFormulaRequest) => Promise<void>;
  onDelete: (_id: number) => Promise<void>;
  loading?: boolean;
  className?: string;
}

const FateFormulaLibrary: React.FC<FateFormulaLibraryProps> = ({
  formulas,
  readOnly = false,
  search,
  onSearch,
  onCreate,
  onUpdate,
  onDelete,
  loading = false,
  className = "",
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingFormula, setEditingFormula] = useState<FateFormula | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const handleSave = async (data: FateFormulaRequest) => {
    setFormLoading(true);
    try {
      if (editingFormula) {
        await onUpdate(editingFormula.id, data);
      } else {
        await onCreate(data);
      }
      setShowForm(false);
      setEditingFormula(null);
    } finally {
      setFormLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingFormula(null);
  };

  const startEdit = (formula: FateFormula) => {
    setEditingFormula(formula);
    setShowForm(true);
  };

  const confirmDelete = async () => {
    if (deleteId !== null) {
      await onDelete(deleteId);
      setDeleteId(null);
    }
  };

  return (
    <div className={`bg-zinc-900 border border-zinc-800 ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-1">
          <FontAwesomeIcon icon={faDice} className="text-amber-400" />
          <h2 className="text-sm font-semibold text-amber-400 tracking-wide uppercase">
            Fate Formulas
          </h2>
        </div>
        {/* Search */}
        <input
          type="text"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search formulas…"
          className="bg-zinc-800 border border-zinc-700 text-zinc-200 text-sm px-3 py-1.5 placeholder-zinc-500 focus:outline-none focus:border-amber-400 w-48"
        />
        {!readOnly && (
          <button
            onClick={() => setShowForm(true)}
            disabled={loading || showForm}
            className="px-3 py-1.5 text-xs bg-amber-400 text-zinc-950 font-semibold hover:bg-amber-300 transition-colors disabled:opacity-50 flex items-center gap-1 flex-shrink-0"
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Fate Formula
          </button>
        )}
      </div>

      {/* Inline form */}
      {showForm && (
        <div className="px-6 py-4 border-b border-zinc-800">
          <DiceTemplateForm
            template={editingFormula ? toTemplate(editingFormula) : undefined}
            isEditing={!!editingFormula}
            onSave={handleSave}
            onCancel={handleCancel}
            loading={formLoading}
          />
        </div>
      )}

      {/* Formula rows */}
      {formulas.length === 0 ? (
        <div className="px-6 py-8 text-center">
          <FontAwesomeIcon icon={faDice} className="text-zinc-600 text-3xl mb-3" />
          <p className="text-zinc-400">No fate formulas yet</p>
          <p className="text-zinc-500 text-sm mt-1">
            Add your first fate formula to build your personal library
          </p>
        </div>
      ) : (
        <div className="divide-y divide-zinc-800">
          {formulas.map((formula) => {
            const diceLabel = formula.diceNotation.match(/d\d+/i)?.[0] || formula.diceNotation;
            return (
              <div
                key={formula.id}
                className="px-6 py-4 flex items-center justify-between hover:bg-zinc-800/30 transition-colors"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 bg-amber-400/20 border border-amber-400/40 flex items-center justify-center text-amber-400 font-mono text-xs font-bold flex-shrink-0">
                    {diceLabel}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white">{formula.name}</p>
                    <p className="text-xs text-zinc-500 flex items-center gap-1 flex-wrap">
                      <span>{formula.diceNotation}</span>
                      {formula.modifier !== 0 && (
                        <span>
                          {formula.modifier > 0 ? `+${formula.modifier}` : formula.modifier}
                        </span>
                      )}
                      <span className="text-zinc-600">·</span>
                      <span>{formula.skillType}</span>
                    </p>
                    {formula.description && (
                      <p className="text-xs text-zinc-600 mt-0.5 truncate">{formula.description}</p>
                    )}
                  </div>
                </div>
                {!readOnly && (
                  <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                    <button
                      onClick={() => startEdit(formula)}
                      disabled={loading || showForm}
                      title="Edit"
                      className="px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 text-zinc-400 hover:bg-zinc-700 transition-colors disabled:opacity-50"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      onClick={() => setDeleteId(formula.id)}
                      disabled={loading || showForm}
                      title="Delete"
                      className="px-3 py-1.5 text-xs bg-red-900/40 border border-red-800/60 text-red-400 hover:bg-red-900/60 transition-colors disabled:opacity-50"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmationDialog
        isOpen={deleteId !== null}
        title="Delete Fate Formula"
        message="Are you sure you want to delete this fate formula?"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
        variant="danger"
      />
    </div>
  );
};

export default FateFormulaLibrary;
